import { useEffect, useRef, useState } from 'react';
import { Ban, Plus, Upload, Trash2 } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { dncAPI } from '../services/api.js';
import { formatPhone, formatDateTime } from '../lib/format.js';

// Tenant-scoped Do-Not-Call list (TCPA / legal opt-out). The dialer scrubs
// this list before EVERY campaign call — any number here is never dialed, from
// any campaign. Admins add/remove manually or bulk-import a CSV. Numbers also
// land here automatically when a call is classified as a clear opt-out.

const REASON_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'out_of_business', label: 'Out of business' },
  { value: 'complaint', label: 'Complaint' }
];

// Parse a pasted/uploaded CSV into [{ phoneE164, reason?, notes? }]. Accepts
// either a single phone-per-line, or columns phone[,reason[,notes]]. A header
// row containing "phone" is skipped. Forgiving by design — the server
// normalizes + validates each row and reports invalids.
function parseCsv(text) {
  const out = [];
  for (const rawLine of String(text).split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const cols = line.split(',').map((c) => c.trim());
    if (/^phone/i.test(cols[0])) continue; // header
    const phoneE164 = cols[0];
    if (!phoneE164) continue;
    out.push({
      phoneE164,
      reason: cols[1] || undefined,
      notes: cols[2] || undefined
    });
  }
  return out;
}

export default function DoNotCall() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ phoneE164: '', reason: 'manual', notes: '' });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await dncAPI.list({ limit: 1000 });
      setItems(r?.data || []);
      setTotal(r?.total || 0);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load the Do-Not-Call list.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const phone = draft.phoneE164.trim();
    if (!phone) return;
    setError('');
    try {
      await dncAPI.add({
        phoneE164: phone,
        reason: draft.reason || undefined,
        notes: draft.notes.trim() || undefined
      });
      setAdding(false);
      setDraft({ phoneE164: '', reason: 'manual', notes: '' });
      await load();
    } catch (err) {
      setError(err.body?.error === 'invalid_phone'
        ? 'That phone number could not be parsed.'
        : (err.message || 'Add failed.'));
    }
  };

  const remove = async (phoneE164) => {
    if (!confirm(`Remove ${formatPhone(phoneE164)} from the Do-Not-Call list? It will become dialable again.`)) return;
    setError('');
    try {
      await dncAPI.remove(phoneE164);
      setItems((rows) => rows.filter((r) => r.phoneE164 !== phoneE164));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      setError(err.message || 'Remove failed.');
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setError('');
    setNotice('');
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) { setError('No rows found in that CSV.'); return; }
      const r = await dncAPI.import(rows);
      const { added = 0, invalid = 0, total: t = rows.length } = r?.data || {};
      setNotice(`Imported ${added} number${added === 1 ? '' : 's'} from ${t} row${t === 1 ? '' : 's'}${invalid ? ` · ${invalid} invalid/skipped` : ''}.`);
      await load();
    } catch (err) {
      setError(err.message || 'CSV import failed.');
    }
  };

  return (
    <div className="px-6 md:px-10 py-8 max-w-5xl">
      <PageHeader
        title="Do-Not-Call list"
        subtitle="Legal opt-outs. Any number here is suppressed before every campaign call — across all campaigns."
        actions={
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={onFile}
            />
            <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()}>
              <Upload size={14} /> Import CSV
            </button>
            <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
              <Plus size={14} /> Add number
            </button>
          </div>
        }
      />

      {error && <div className="mb-6"><ErrorBanner message={error} /></div>}
      {notice && (
        <div className="mb-6 rounded-lg border border-emerald-700/40 bg-emerald-900/20 px-4 py-3 text-sm text-emerald-200">
          {notice}
        </div>
      )}

      {adding && (
        <div className="card mb-6 border-alfred-gold/30">
          <h2 className="text-stone-100 font-medium mb-3">Add a number to Do-Not-Call</h2>
          <div className="space-y-3">
            <input
              className="field-input"
              placeholder="Phone (e.g. +17135551234)"
              value={draft.phoneE164}
              onChange={(e) => setDraft({ ...draft, phoneE164: e.target.value })}
              autoFocus
            />
            <select
              className="field-input"
              value={draft.reason}
              onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            >
              {REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <input
              className="field-input"
              placeholder="Notes (optional)"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
            <div className="flex gap-2">
              <button type="button" className="btn-primary" onClick={add}>Add</button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => { setAdding(false); setDraft({ phoneE164: '', reason: 'manual', notes: '' }); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-800 text-xs uppercase tracking-wider text-stone-500">
          {total} number{total === 1 ? '' : 's'} on the list
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-800">
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Reason</th>
              <th className="px-4 py-3 font-medium">Notes</th>
              <th className="px-4 py-3 font-medium">Added</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-stone-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-stone-500">
                No numbers on the Do-Not-Call list. Add one above, import a CSV, or let opt-outs land here automatically.
              </td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-b border-stone-900 hover:bg-stone-900/40">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Ban size={14} className="text-red-400" />
                    <span className="text-stone-100 font-medium tabular-nums">{formatPhone(r.phoneE164)}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-300">{r.reason || '—'}</td>
                <td className="px-4 py-3 text-stone-400 max-w-sm truncate" title={r.notes || ''}>{r.notes || '—'}</td>
                <td className="px-4 py-3 text-stone-500 whitespace-nowrap">{formatDateTime(r.createdAt)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => remove(r.phoneE164)}
                    className="text-red-300 hover:text-red-200 p-1"
                    aria-label="Remove from Do-Not-Call"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
